import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Textarea } from "@/shared/components/ui/textarea";
import { Label } from "@/shared/components/ui/label";
import { Switch } from "@/shared/components/ui/switch";
import { Loader2, Sparkles } from "lucide-react";
import { supabase } from "@/shared/integrations/supabase/client";
import { useToast } from "@/shared/hooks/use-toast";
import { ToastAction } from "@/shared/components/ui/toast";
import { cn } from "@/shared/lib/utils";
import HelpTooltip from "@/shared/components/HelpTooltip";
import { fetchLabelProcessoVinculo, hrefProcessoVinculo } from "./correioVinculoHelpers";

function montarTextoAndamento(resumo: string, decisoes: string, proximosPassos: string): string {
  const parts: string[] = [];
  const r = resumo.trim();
  if (r) parts.push(r);
  const d = decisoes.trim();
  if (d) parts.push(`Decisões\n${d}`);
  const p = proximosPassos.trim();
  if (p) parts.push(`Próximos passos\n${p}`);
  return parts.join("\n\n");
}

export interface ResumoIAModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resumoId: string;
  titulo: string;
  resumo: string;
  decisoes: string | null;
  proximosPassos: string | null;
  modelo: string;
  vinculoTipo: string;
  vinculoId: string;
  threadId: string;
  /** Assunto da thread (exibido no card informativo). */
  threadAssunto?: string;
  onAprovado?: () => void;
}

const ResumoIAModal = ({
  open,
  onOpenChange,
  resumoId,
  titulo: tituloInicial,
  resumo: resumoInicial,
  decisoes: decisoesInicial,
  proximosPassos: proximosInicial,
  modelo,
  vinculoTipo,
  vinculoId,
  threadId,
  threadAssunto,
  onAprovado,
}: ResumoIAModalProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [titulo, setTitulo] = useState(tituloInicial);
  const [resumo, setResumo] = useState(resumoInicial);
  const [decisoes, setDecisoes] = useState(decisoesInicial ?? "");
  const [proximosPassos, setProximosPassos] = useState(proximosInicial ?? "");
  const [visivelExterno, setVisivelExterno] = useState(false);
  const [processoLabel, setProcessoLabel] = useState<string | null>(null);
  const [aprovando, setAprovando] = useState(false);
  const [rejeitando, setRejeitando] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTitulo(tituloInicial);
    setResumo(resumoInicial);
    setDecisoes(decisoesInicial ?? "");
    setProximosPassos(proximosInicial ?? "");
    setVisivelExterno(false);
  }, [open, tituloInicial, resumoInicial, decisoesInicial, proximosInicial]);

  useEffect(() => {
    if (!open || !vinculoTipo || !vinculoId) {
      setProcessoLabel(null);
      return;
    }
    void fetchLabelProcessoVinculo(vinculoTipo, vinculoId).then(setProcessoLabel);
  }, [open, vinculoTipo, vinculoId]);

  const hrefPosAprovar = hrefProcessoVinculo(vinculoTipo, vinculoId);

  const handleAprovar = async () => {
    const tituloFinal = titulo.trim() || tituloInicial;
    const resumoFinal = montarTextoAndamento(resumo, decisoes, proximosPassos);
    if (!resumoFinal.trim()) {
      toast({ title: "Resumo vazio", description: "Preencha o resumo executivo antes de aprovar.", variant: "destructive" });
      return;
    }
    setAprovando(true);
    try {
      const { data, error } = await supabase.rpc("aprovar_resumo_ia", {
        p_resumo_id: resumoId,
        p_titulo_final: tituloFinal,
        p_resumo_final: resumoFinal,
        p_visivel_externo: visivelExterno,
      });
      if (error) {
        toast({ title: "Erro ao aprovar", description: error.message, variant: "destructive" });
        return;
      }
      const result = data as { sucesso?: boolean; erro?: string };
      if (result?.erro) {
        toast({ title: "Não foi possível aprovar", description: result.erro, variant: "destructive" });
        return;
      }
      onOpenChange(false);
      onAprovado?.();
      toast({
        title: "Andamento registrado",
        description: hrefPosAprovar ? "O resumo foi convertido em andamento no processo." : "Resumo aprovado.",
        action: hrefPosAprovar ? (
          <ToastAction
            altText="Ver processo"
            onClick={() => {
              navigate(hrefPosAprovar);
            }}
          >
            Ver processo →
          </ToastAction>
        ) : undefined,
      });
    } finally {
      setAprovando(false);
    }
  };

  const handleRejeitar = async () => {
    setRejeitando(true);
    try {
      const { error } = await supabase.rpc("rejeitar_resumo_ia", { p_resumo_id: resumoId });
      if (error) {
        toast({ title: "Erro ao rejeitar", description: error.message, variant: "destructive" });
        return;
      }
      onOpenChange(false);
      toast({
        title: "Resumo rejeitado",
        description: "A thread foi reaberta para nova conclusão quando aplicável.",
      });
    } finally {
      setRejeitando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "max-w-[600px] w-[calc(100%-1.5rem)] max-h-[min(90dvh,720px)] overflow-y-auto",
          "border-[rgba(169,171,174,0.35)] bg-[#f9f6f0] rounded-xl"
        )}
      >
        <DialogHeader className="space-y-2 pr-8">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <div className="h-9 w-9 rounded-full bg-[#791614]/10 flex items-center justify-center shrink-0 border border-[#d7b369]/40">
                <Sparkles className="h-4 w-4 text-[#791614]" />
              </div>
              <div className="min-w-0">
                <DialogTitle className="font-display text-lg text-[#791614] leading-tight">
                  Resumo gerado por IA
                </DialogTitle>
                <DialogDescription className="text-xs text-muted-foreground">
                  Revise e edite antes de registrar o andamento no processo vinculado.
                </DialogDescription>
              </div>
            </div>
            <span className="font-accent text-[9px] uppercase tracking-widest text-[#a9abae] shrink-0 pt-1 max-w-[100px] text-right leading-tight">
              {modelo}
            </span>
          </div>
        </DialogHeader>

        <div className="rounded-lg border border-[rgba(169,171,174,0.35)] bg-[#f9f6f0] p-3 space-y-2 text-xs">
          <p>
            <span className="font-accent text-[9px] uppercase tracking-widest text-muted-foreground">Modelo IA</span>
            <span className="ml-2 text-foreground">{modelo}</span>
          </p>
          <p>
            <span className="font-accent text-[9px] uppercase tracking-widest text-muted-foreground">Thread</span>
            <span className="ml-2 text-foreground break-words">{threadAssunto || threadId.slice(0, 8) + "…"}</span>
          </p>
          <p>
            <span className="font-accent text-[9px] uppercase tracking-widest text-muted-foreground">Processo</span>
            <span className="ml-2 text-foreground break-words">
              {processoLabel || (vinculoId ? `${vinculoTipo || "—"} · ${vinculoId.slice(0, 8)}…` : "—")}
            </span>
          </p>
        </div>

        <div className="space-y-4 py-1">
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Título do andamento</Label>
            <p className="text-[11px] text-muted-foreground">Aparece como título da movimentação no processo.</p>
            <Input
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              className="bg-[#f9f6f0] border-[rgba(169,171,174,0.35)]"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Resumo executivo</Label>
            <p className="text-[11px] text-muted-foreground">Síntese principal da conversa; pode editar livremente.</p>
            <Textarea
              value={resumo}
              onChange={(e) => setResumo(e.target.value)}
              rows={5}
              className="bg-[#f9f6f0] border-[rgba(169,171,174,0.35)] resize-y min-h-[100px]"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Decisões</Label>
            <p className="text-[11px] text-muted-foreground">Decisões sugeridas pela IA; serão anexadas ao texto do andamento.</p>
            <Textarea
              value={decisoes}
              onChange={(e) => setDecisoes(e.target.value)}
              rows={3}
              className="bg-[#f9f6f0] border-[rgba(169,171,174,0.35)] resize-y"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Próximos passos</Label>
            <p className="text-[11px] text-muted-foreground">Ações recomendadas; também entram no corpo do andamento.</p>
            <Textarea
              value={proximosPassos}
              onChange={(e) => setProximosPassos(e.target.value)}
              rows={3}
              className="bg-[#f9f6f0] border-[rgba(169,171,174,0.35)] resize-y"
            />
          </div>

          <div
            className={cn(
              "rounded-lg border p-3 transition-colors",
              visivelExterno ? "border-[#d7b369] bg-[#d7b369]/[0.08] shadow-[0_0_0_1px_rgba(215,179,105,0.25)]" : "border-[rgba(169,171,174,0.35)]"
            )}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <Label htmlFor="visivel-externo-resumo" className="text-sm cursor-pointer">
                  Visível no portal externo
                </Label>
                <HelpTooltip text="Quando ativo, o andamento pode ser exibido ao síndico ou gestor no portal da instituição, conforme regras do módulo." />
              </div>
              <Switch id="visivel-externo-resumo" checked={visivelExterno} onCheckedChange={setVisivelExterno} />
            </div>
            {visivelExterno && (
              <p className="text-xs text-[#791614] mt-2 font-medium">
                O síndico ou gestor poderá ver este andamento no portal externo, quando aplicável ao processo.
              </p>
            )}
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-2 sm:justify-end pt-2 border-t border-border/40">
          <Button
            type="button"
            variant="outline"
            className="border-[#791614]/35 bg-[#f9f6f0] w-full sm:w-auto order-2 sm:order-1"
            disabled={aprovando || rejeitando}
            onClick={() => void handleRejeitar()}
          >
            {rejeitando ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Rejeitar e reabrir thread
          </Button>
          <Button
            type="button"
            variant="bordeaux"
            className="w-full sm:w-auto order-1 sm:order-2"
            disabled={aprovando || rejeitando}
            onClick={() => void handleAprovar()}
          >
            {aprovando ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Aprovar e registrar andamento
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ResumoIAModal;
